import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogComponent } from '../../../../shared/components/dialog/dialog.component';
import { Agency } from '../../models/agency.model';
import { TransactionService } from '../../services/transaction.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { ProfileService } from '../../../../core/services/profile/profile.service';

@Component({
  selector: 'app-create-transaction-dialog',
  standalone: true,
  imports: [CommonModule, DialogComponent, ReactiveFormsModule, SpinnerComponent],
  templateUrl: './create-transaction-dialog.component.html'
})
export class CreateTransactionDialogComponent implements OnChanges {
  @Input() show = false;
  @Input() showHeader: boolean = true;
  @Output() close = new EventEmitter<void>();
  @Output() submitTransaction = new EventEmitter<{ fileNumber: string; agency: Agency | null }>();
  @Output() error = new EventEmitter<{ error?: any }>();

  form: FormGroup;
  selectedAgency: Agency | null = null;
  agencies: Agency[] = [];
  groupedAgencies: { agencyLegalName: string, agencies: Agency[] }[] = [];
  agenciesLoading = false;
  phoneTooltipVisible = false;
  submitting = false;
  isLoading = false;
  phoneInvalid: boolean = false;
  phoneErrorMessage: string = '';
  phoneTouched: boolean = false;
  @ViewChild('phoneInput') phoneInputRef!: ElementRef<HTMLInputElement>;
  
  get isApprovedAttorney(): boolean {
    return this.profileService.isApprovedAttorney;
  }
  
  get isSubmitEnabled(): boolean {
    // Phone is not required, but if provided, must be exactly 10 digits
    const phoneValue = this.form.get('phone')?.value || '';
    const phoneDigits = phoneValue.replace(/\D/g, '');

    let phoneValid = true;
    if (phoneDigits.length > 0) {
      phoneValid = phoneDigits.length === 10;
    }

    return this.form.valid && phoneValid;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly transactionService: TransactionService,
    private readonly profileService: ProfileService
  ) {
    this.form = this.fb.group({
      agencyId: [null, this.isApprovedAttorney ? [] : [Validators.required]],
      fileNumber: ['', Validators.required],
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['']
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && changes['show'].currentValue === true) {
      this.resetDialog();
      this.loadAgencies();
    }
  }
  resetDialog() {
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.selectedAgency = null;
    this.agencies = [];
    this.groupedAgencies = [];
    // Reset phone validation state
    this.phoneInvalid = false;
    this.phoneErrorMessage = '';
    this.phoneTouched = false;
  }

  loadAgencies() {
    // Don't load agencies for approved attorneys
    if (this.isApprovedAttorney) {
      this.agenciesLoading = false;
      this.isLoading = false;
      return;
    }
    
    this.isLoading = true;
    this.agenciesLoading = true;
    this.transactionService.getIssuingAgents().subscribe({
      next: (agencies) => {
        this.agencies = agencies;
        this.groupedAgencies = this.groupAgencies(agencies);
        this.agenciesLoading = false;
        this.isLoading = false;
      },
      error: () => {
        this.agencies = [];
        this.groupedAgencies = [];
        this.agenciesLoading = false;
        this.isLoading = false;
      }
    });
  }

  onSubmit() {
    if (this.form.valid && !this.submitting) {
      this.isLoading = true;
      this.submitting = true;
      const value = this.form.value;
      const rawPhone = value.phone?.replace(/\D/g, '') ?? '';
      const hasPhoneInput = value.phone && value.phone.trim() !== '';

      let fullPhoneNumber: string | null = null;

      if (hasPhoneInput) {
        fullPhoneNumber = '+1' + rawPhone;
      }

      const payload = {
        customerAccountId: this.isApprovedAttorney ? null : Number(value.agencyId),
        fileNumber: value.fileNumber,
        identityVerification: {
          firstName: value.firstName,
          middleName: value.middleName,
          lastName: value.lastName,
          email: value.email,
          phoneNumber: fullPhoneNumber
        }
      };
      this.transactionService.createTransaction(payload).subscribe({
        next: () => {
          this.submitting = false;
          this.submitTransaction.emit({
            fileNumber: payload.fileNumber,
            agency: this.selectedAgency
          });
          
          this.isLoading = false;
          this.close.emit();
        },
        error: (errorResponse) => {
          this.error.emit(errorResponse?.error);
          this.submitting = false;
          this.isLoading = false;
        }
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  onClose() {
    this.close.emit();
  }

  onAgencySelect(event: Event) {
    const select = event.target as HTMLSelectElement;
    const agencyId = +select.value;
    this.selectedAgency = this.agencies.find(a => a.issuingAgentId === agencyId) || null;
  }

  groupAgencies(agencies: Agency[]) {
    const groups: { [key: string]: Agency[] } = {};

    for (const agency of agencies) {
      const group = agency.issuingAgentName || agency.agencyLegalName;
      const accountNumberParts = agency.accountNumber.split('.');
      const locationNumber = `${accountNumberParts[0]}.${accountNumberParts[1]}`;
      const agencyLegalName = `${group} - ${locationNumber}`;

      if (!groups[agencyLegalName]) {
        groups[agencyLegalName] = [];
      }
      groups[agencyLegalName].push(agency);
    }

    return Object.entries(groups).map(([agencyLegalName, agencies]) => ({
      agencyLegalName,
      agencies
    }));
  }
  onPhoneInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const phoneValue = input.value;
    // Apply the US/CA phone number formatting
    this.formatPhoneNumber(phoneValue);
    this.validatePhone();
  }
  formatPhoneNumber(value: string) {
    const inputEl = this.phoneInputRef?.nativeElement;
    let formatted = value.replace(/\D/g, ''); // Remove non-digits

    // If no digits, set empty string instead of formatting with underscores
    if (formatted.length === 0) {
      this.form.get('phone')?.setValue('', { emitEvent: false });
      return;
    }

    // US/CA format: (XXX) XXX-XXXX
    formatted = formatted.slice(0, 10);
    const padded = formatted.padEnd(10, '_');
    formatted = `(${padded.slice(0, 3)}) ${padded.slice(3, 6)}-${padded.slice(6, 10)}`;

    this.form.get('phone')?.setValue(formatted, { emitEvent: false });

    // Restore caret position
    setTimeout(() => {
      if (inputEl) {
        // Try to keep caret before the first _ after typing
        const nextUnderscore = formatted.indexOf('_');
        if (nextUnderscore !== -1) {
          inputEl.setSelectionRange(nextUnderscore, nextUnderscore);
        } else {
          inputEl.setSelectionRange(formatted.length, formatted.length);
        }
      }
    });
  }
  validatePhone() {
    const phoneValue = this.form.get('phone')?.value || '';
    const phoneDigits = phoneValue.replace(/\D/g, '');

    this.phoneInvalid = false;
    this.phoneErrorMessage = '';

    // Phone is optional, but if provided, must be exactly 10 digits
    if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
      this.phoneInvalid = true;
      this.phoneErrorMessage = 'Please enter a valid phone number';
    }
  }

  onPhoneBlur() {
    this.phoneTouched = true;
  }

  onPhoneFocus() {
    this.phoneTouched = false;
  }

  onPhoneMouseEnter() {
    if (!this.phoneInvalid) {
      this.phoneTooltipVisible = true;
    }
  }

  onPhoneMouseLeave() {
    this.phoneTooltipVisible = false;
  }
} 